import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import "./App.css";
import BrandName from './components/BrandName';
import { 
  trackPageView, 
  trackQuoteSubmission, 
  trackOrderCreation, 
  trackAddToCart, 
  trackDesignView,
  trackContactFormSubmission,
  trackServiceRequest,
  trackUserAuth,
  trackDesignerApplication,
  trackDesignUpload,
  loadMetaPixel,
  trackPixelPageView
} from './utils/analytics';
import { Upload, FileText, Zap, Settings, ChevronRight, ChevronDown, Check, CheckCircle, Mail, Phone, MapPin, ArrowLeft, User, Users, LogIn, ShoppingCart, Package, Eye, Edit, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider, useCurrency } from "./contexts/CurrencyContext";
import { CartProvider, useCart } from "./contexts/CartContext";
import Profile from "./components/Profile";
import LoginModal from "./components/LoginModal";
import MainNavigation from "./components/MainNavigation";
import ProductsPage from "./components/ProductsPage";
import Footer from "./components/Footer";
import ProductDetailPage from "./components/ProductDetailPage";
import AdminPanel from "./components/AdminPanel";
import CartPage from "./components/CartPage";
import ThreeDPreview from "./components/ThreeDPreview";
import SharedPageLayout from "./components/SharedPageLayout";
import ComingSoon from "./components/ComingSoon";
import CurrencySelector from "./components/CurrencySelector";
import FloatingCart from "./components/FloatingCart";
import { BACKEND_URL } from "./config";
import { termsOfService, shippingReturns, licensingPolicy, privacyPolicy } from "./data/policies";
import { TermsOfServiceRoute, ShippingReturnsRoute, LicensingPolicyRoute, PrivacyPolicyRoute } from "./pages/PolicyPages";
import { FAQRoute, TermsRoute, FileFormatsRoute, MaterialGuideRoute, QualityStandardsRoute, ShippingInfoRoute } from "./pages/InfoPages";
import { ProductsRoute, ProductDetailRoute, CartRoute, ProfileRoute, AdminRoute, AboutRoute } from "./pages/SimpleRoutes";
import { ServicesRoute, ContactRoute } from "./pages/FeatureRoutes";
import { AdminMarketplaceRoute, MarketplaceRoute, ListingDetailRoute, DesignerEditRoute } from "./pages/MarketplaceRoutes";
import { DesignerApplyRoute, DesignerDashboardRoute, DesignerUploadRoute, DesignerTermsRoute, CustomerLicenseTermsRoute } from "./pages/DesignerRoutes";
import { ComingSoonPage, AppContent, CreatorProfileRoute } from "./pages/HomePage";
import DesignerStorefront from "./pages/DesignerStorefront";
import ProfileQuickActionMockups from "./pages/ProfileQuickActionMockups";
import DesignerInfoPage from "./pages/DesignerInfoPage";
import ServicesToolPage from "./pages/ServicesToolPage";
import InstantQuoteLanding from "./pages/InstantQuoteLanding";
import CustomDesignLanding from "./pages/CustomDesignLanding";

// Use runtime-detected backend URL (handles different deployment environments)
const API_BASE = BACKEND_URL;
const API = `${API_BASE}/api`;

// Google Analytics Page View Tracker
// Also sets a per-route document.title so GA4's default "Pages and screens"
// report (which groups by page_title) shows meaningful per-page rows — without
// this, every route has the same boilerplate title and all traffic collapses
// into one row.
const BASE_TITLE = 'Create 3D Works';

const titleForPath = (pathname) => {
  // Exact routes first
  const exact = {
    '/':                 'Create 3D Works — Professional 3D Printing Services',
    '/about':            `About — ${BASE_TITLE}`,
    '/services':         `Services — ${BASE_TITLE}`,
    '/contact':          `Contact — ${BASE_TITLE}`,
    '/faq':              `FAQ — ${BASE_TITLE}`,
    '/products':         `Shop — ${BASE_TITLE}`,
    '/cart':             `Your Cart — ${BASE_TITLE}`,
    '/profile':          `Your Profile — ${BASE_TITLE}`,
    '/admin':            `Admin — ${BASE_TITLE}`,
    '/marketplace':      `Designs — ${BASE_TITLE}`,
    '/instant-quote':    `Instant 3D Print Quote — ${BASE_TITLE}`,
    '/custom-design':    `Custom 3D Design Service — ${BASE_TITLE}`,
    '/get-started':      `Get Started — ${BASE_TITLE}`,
    '/terms':            `Terms of Service — ${BASE_TITLE}`,
    '/terms-of-service': `Terms of Service — ${BASE_TITLE}`,
    '/privacy':          `Privacy Policy — ${BASE_TITLE}`,
    '/privacy-policy':   `Privacy Policy — ${BASE_TITLE}`,
    '/designer/info':    `Become a Designer — ${BASE_TITLE}`,
    '/designer/apply':   `Designer Application — ${BASE_TITLE}`,
    '/designer/dashboard': `Designer Dashboard — ${BASE_TITLE}`,
    '/designer/upload':  `Upload Design — ${BASE_TITLE}`,
    '/designer/terms':   `Designer Terms — ${BASE_TITLE}`,
  };
  if (exact[pathname]) return exact[pathname];

  // Dynamic patterns
  if (pathname.startsWith('/start/')) {
    const slug = pathname.replace('/start/', '').replace(/-/g, ' ');
    return `${slug.charAt(0).toUpperCase() + slug.slice(1)} — ${BASE_TITLE}`;
  }
  if (pathname.startsWith('/product/')) return `Product — ${BASE_TITLE}`;
  if (pathname.startsWith('/marketplace/listing/')) return `Design — ${BASE_TITLE}`;
  if (pathname.startsWith('/creator/') || pathname.startsWith('/designer/')) return `Designer — ${BASE_TITLE}`;
  if (pathname.startsWith('/admin/')) return `Admin — ${BASE_TITLE}`;
  return BASE_TITLE;
};

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Set the per-route title BEFORE firing trackers so they pick it up.
    const newTitle = titleForPath(location.pathname);
    if (document.title !== newTitle) document.title = newTitle;
    // GA4 pageview with correct path + title
    trackPageView(location.pathname + location.search, newTitle);
    // Mirror the same page view to Meta Pixel (fires only if pixel is loaded)
    trackPixelPageView();
  }, [location]);

  return null; // This component doesn't render anything
};

// One-shot Meta Pixel loader. Reads the public tracking-settings endpoint on
// mount and injects fbevents.js when a pixel ID is configured by an admin.
const MetaPixelLoader = () => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/site/tracking-settings`);
        if (!res.ok) return;
        const data = await res.json();
        const pixelId = (data && data.meta_pixel_id) || '';
        if (!cancelled && pixelId) {
          loadMetaPixel(pixelId);
        }
      } catch (e) {
        // Tracking is best-effort — never block the app on a failed fetch
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if coming soon mode is enabled
    const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'false';
    if (!comingSoonEnabled) return true; // If not in coming soon mode, allow access
    
    // Check if already authenticated (persists across sessions)
    return localStorage.getItem('site_authenticated') === 'true';
  });

  const handleAuthenticate = () => {
    setIsAuthenticated(true);
  };

  // Check if coming soon mode is enabled
  const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'true';

  // If coming soon is enabled and user is not authenticated, show password page
  if (comingSoonEnabled && !isAuthenticated) {
    return <ComingSoon onAuthenticated={handleAuthenticate} />;
  }

  // Otherwise, show the full app
  return (
    <Router>
      <PageViewTracker />
      <MetaPixelLoader />
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
            <CurrencySelector />
            <FloatingCart />
            <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/get-started" element={<ServicesToolPage />} />
            {/* Clean per-service URLs for Google Ads landing pages — each slug
                deep-links straight into one wizard so ads can track per-service
                traffic without query-string noise. */}
            <Route path="/start/:slug" element={<ServicesToolPage />} />
            <Route path="/instant-quote" element={<InstantQuoteLanding />} />
            <Route path="/custom-design" element={<CustomDesignLanding />} />
            <Route path="/products" element={<ProductsRoute />} />
            <Route path="/product/:slug" element={<ProductDetailRoute />} />
            <Route path="/cart" element={<CartRoute />} />
            <Route path="/profile" element={<ProfileRoute />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/about" element={<AboutRoute />} />
            <Route path="/services" element={<ServicesRoute />} />
            <Route path="/contact" element={<ContactRoute />} />
            <Route path="/marketplace" element={<MarketplaceRoute />} />
            <Route path="/marketplace/listing/:id" element={<ListingDetailRoute />} />
            <Route path="/creator/:username" element={<DesignerStorefront />} />
            <Route path="/profile-quick-action-mockups" element={<ProfileQuickActionMockups />} />
            <Route path="/designer/:username" element={<DesignerStorefront />} />
            <Route path="/designer/info" element={<DesignerInfoPage />} />
            <Route path="/designer/apply" element={<DesignerApplyRoute />} />
            <Route path="/designer/dashboard" element={<DesignerDashboardRoute />} />
            <Route path="/designer/upload" element={<DesignerUploadRoute />} />
            <Route path="/designer/edit/:id" element={<DesignerEditRoute />} />
            <Route path="/designer/terms" element={<DesignerTermsRoute />} />
            <Route path="/customer/license-terms" element={<CustomerLicenseTermsRoute />} />
            <Route path="/admin/marketplace" element={<AdminMarketplaceRoute />} />
            <Route path="/faq" element={<FAQRoute />} />
            <Route path="/terms" element={<TermsRoute />} />
            <Route path="/file-formats" element={<FileFormatsRoute />} />
            <Route path="/material-guide" element={<MaterialGuideRoute />} />
            <Route path="/quality-standards" element={<QualityStandardsRoute />} />
            <Route path="/shipping-info" element={<ShippingInfoRoute />} />
            
            {/* Policy Pages */}
            <Route path="/terms-of-service" element={<TermsOfServiceRoute />} />
            <Route path="/shipping-returns" element={<ShippingReturnsRoute />} />
            <Route path="/licensing-policy" element={<LicensingPolicyRoute />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </Router>
  );
};

// Utility function to create SEO-friendly slugs
const createProductSlug = (manufacturer, productName) => {
  const combined = `${manufacturer} ${productName}`;
  return combined
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim('-'); // Remove leading/trailing hyphens
};

// Utility function to parse slug back to search terms
const parseProductSlug = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Cart Context moved to /contexts/CartContext.js
// HomePage components (ComingSoonPage, AppContent, CreatorProfileRoute) moved to /pages/HomePage.js
// Designer routes (Apply, Dashboard, Upload, Terms, CustomerLicenseTerms) moved to /pages/DesignerRoutes.js

export default App;
